import { useCallback, useEffect, useState, type FormEvent } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Send } from "lucide-react";
import { ghostButtonClass, primaryButtonClass, Status, textareaClass } from "../components/ui";
import { errorMessage, request } from "../lib/api";
import { supportDate, type SupportTicketDetail as TicketDetail } from "../support/types";

export function SupportTicketDetail() {
  const { t, i18n } = useTranslation();
  const { ticketID = "" } = useParams();
  const location = useLocation();
  const [data, setData] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [replyError, setReplyError] = useState("");
  const [sending, setSending] = useState(false);

  const reload = useCallback(async () => {
    const result = await request<TicketDetail>(`/api/support/tickets/${ticketID}`);
    setData(result);
  }, [ticketID]);

  useEffect(() => {
    request<TicketDetail>(`/api/support/tickets/${ticketID}`)
      .then(setData)
      .catch((cause) => setError(errorMessage(cause)))
      .finally(() => setLoading(false));
  }, [ticketID]);

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reply.trim() || sending) return;
    setSending(true);
    setReplyError("");
    try {
      await request(`/api/support/tickets/${ticketID}/messages`, {
        method: "POST",
        body: JSON.stringify({ body: reply.trim() }),
      });
      setReply("");
      try {
        await reload();
      } catch {
        setReplyError(t("helpReplySavedRefreshFailed"));
      }
    } catch (cause) {
      setReplyError(errorMessage(cause));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-8">
      <NavLink to="/helpline" state={location.state} className={ghostButtonClass}>
        <ChevronLeft size={16} />
        {t("helpYourRequests")}
      </NavLink>
      {loading ? (
        <Status variant="loading" title={t("loading")} message={t("helpLoadingTicket")} />
      ) : error || !data ? (
        <Status variant="error" title={t("helpTicketsUnavailable")} message={error} />
      ) : (
        <>
          <header>
            <span className="inline-flex rounded-full bg-accent-soft px-2.5 py-1 text-xs font-semibold text-accent">
              {t(`helpStatus_${data.ticket.status}`)}
            </span>
            <h1 className="mt-3 text-xl font-bold text-app-ink">{data.ticket.subject}</h1>
            <p className="mt-1 text-xs text-app-muted">
              {t(`helpCategory_${data.ticket.category}`)} ·{" "}
              {supportDate(data.ticket.created_at, i18n.language)}
            </p>
          </header>
          <section className="flex flex-col gap-3" aria-label={t("helpConversation")}>
            <article className="rounded-xl bg-surface p-4 shadow-raised">
              <p className="mb-2 text-xs font-semibold text-app-muted">{t("helpYou")}</p>
              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-app-ink">
                {data.ticket.description}
              </p>
            </article>
            {data.messages.map((message) => (
              <article
                key={message.id}
                className={`rounded-xl p-4 shadow-raised ${message.author_role === "staff" ? "bg-accent-soft" : "bg-surface"}`}
              >
                <p className="mb-2 text-xs font-semibold text-app-muted">
                  {message.author_role === "staff" ? t("helpSupportTeam") : t("helpYou")}
                  {" · "}
                  {supportDate(message.created_at, i18n.language)}
                </p>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-app-ink">
                  {message.body}
                </p>
              </article>
            ))}
          </section>
          <form
            onSubmit={(event) => void send(event)}
            className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-raised"
          >
            <label className="grid gap-2 text-sm font-semibold text-app-ink">
              {t("helpReply")}
              <textarea
                required
                maxLength={4000}
                rows={4}
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder={t("helpReplyPlaceholder")}
                className={textareaClass}
              />
            </label>
            {data.ticket.status === "resolved" && (
              <p className="text-xs text-app-muted">{t("helpReplyReopens")}</p>
            )}
            {replyError && (
              <p role="alert" className="text-sm text-accent-strong">
                {replyError}
              </p>
            )}
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className={`${primaryButtonClass} flex items-center justify-center gap-2`}
            >
              <Send size={17} />
              {sending ? t("helpSending") : t("helpSendReply")}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
